import { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import type { ECharts } from 'echarts';
import worldJson from '../assets/custom.geo.json';
import chinaJson from '../assets/CN.json';

// 状态类型定义
type StatusType = 'UP' | 'DEGRADED' | 'DOWN' | 'LOADING' | 'BUILDING';
type StatusResponse = {
    status: string;
    uptime: string;
    last_updated_at: number;
};

// 注册世界地图和中国地图
echarts.registerMap('world', worldJson as any);
echarts.registerMap('china', chinaJson as any);

// 中国数据中心
const chinaCenters = [
    { name: '北京', value: [116.4551, 40.5539], region: '北京', tag: '' },
    { name: '上海', value: [121.7648, 31.2891], region: '上海', tag: '' },
    { name: '广州', value: [113.2644, 23.1291], region: '广东省', tag: 'GD-DNS' },
    { name: '武汉', value: [114.3896, 30.6628], region: '湖北省', tag: 'HB-DNS' },
    { name: '十堰', value: [110.7874, 32.6475], region: '湖北省', tag: 'HB-DNS' },
    { name: '南京', value: [118.7969, 32.0603], region: '江苏省', tag: 'JS-DNS' },
    { name: '成都', value: [104.0665, 30.5723], region: '四川省', tag: 'SC-DNS' },
    { name: '香港', value: [114.1694, 22.3193], region: '香港', tag: 'HK-DNS' },
];

// 全球数据中心
const globalCenters = [
    { name: '日本', value: [139.6917, 35.6895], region: '亚洲', tag: '' },
    { name: '新加坡', value: [103.8198, 1.3521], region: '亚洲', tag: '' },
    { name: '美国', value: [-95.7129, 37.0902], region: '北美洲', tag: 'US-DNS' },
    { name: '德国', value: [13.4050, 52.5200], region: '欧洲', tag: '' },
    { name: '英国', value: [-0.1278, 51.5074], region: '欧洲', tag: '' },
];

const WorldMap = () => {
    const chartRef = useRef(null);
    const chartInstance = useRef<ECharts | null>(null);
    const [mapType, setMapType] = useState<'world' | 'china'>('china');
    const [statusMap, setStatusMap] = useState<Record<string, StatusType>>({});

    // 获取状态数据
    const fetchStatus = async (tag: string) => {
        try {
            const response = await fetch(`https://status-api.setonink.com/status?tag=${tag}`);
            const data: StatusResponse = await response.json();
            return data.status as StatusType;
        } catch (error) {
            console.error(`Error fetching status for ${tag}:`, error);
            return 'DOWN' as StatusType;
        }
    };

    // 更新所有数据中心状态
    const updateAllStatuses = async () => {
        const centers = mapType === 'world' ? globalCenters : chinaCenters;
        const newStatusMap: Record<string, StatusType> = {};
        
        // 先将所有有 tag 的中心设置为 LOADING，没有 tag 的设置为 BUILDING
        for (const center of centers) {
            newStatusMap[center.tag] = center.tag ? 'LOADING' : 'BUILDING';
        }
        setStatusMap(newStatusMap);

        // 只对有 tag 的中心进行状态查询
        for (const center of centers) {
            if (center.tag) {
                const status = await fetchStatus(center.tag);
                setStatusMap(prev => ({
                    ...prev,
                    [center.tag]: status
                }));
            }
        }
    };

    useEffect(() => {
        updateAllStatuses();
        const interval = setInterval(updateAllStatuses, 300000); // 每5分钟更新一次
        return () => clearInterval(interval);
    }, [mapType]);

    useEffect(() => {
        if (chartRef.current) {
            chartInstance.current = echarts.init(chartRef.current);

            const option = {
                backgroundColor: 'transparent',
                tooltip: { show: false },
                geo: {
                    map: mapType,
                    roam: false,
                    zoom: mapType === 'world' ? 1.2 : 1.6,
                    center: mapType === 'world' ? [10, 20] : [104, 36],
                    emphasis: {
                        label: { show: false },
                        itemStyle: { areaColor: '#e0e7ff' }
                    },
                    itemStyle: {
                        areaColor: '#f1f5f9',
                        borderColor: '#cbd5e1',
                        borderWidth: 0.5
                    }
                },
                series: [
                    {
                        name: '数据中心',
                        type: 'effectScatter',
                        coordinateSystem: 'geo',
                        data: mapType === 'world' ? globalCenters : chinaCenters,
                        symbolSize: mapType === 'world' ? 10 : 12,
                        showEffectOn: 'render',
                        rippleEffect: {
                            brushType: 'stroke',
                            scale: mapType === 'world' ? 4 : 5,
                            period: 3
                        },
                        hoverAnimation: false,
                        label: {
                            show: true,
                            position: 'right',
                            formatter: '{b}',
                            fontSize: 12,
                            fontWeight: 'bold',
                            color: '#4338ca',
                            distance: 5,
                            backgroundColor: 'rgba(255,255,255,0.7)',
                            padding: [3, 5],
                            borderRadius: 3
                        },
                        labelLayout: {
                            moveOverlap: 'shiftX'
                        },
                        itemStyle: {
                            color: '#4f46e5',
                            shadowBlur: 10,
                            shadowColor: 'rgba(79,70,229,0.6)',
                            opacity: 0.9,
                            borderColor: '#fff',
                            borderWidth: 2
                        },
                        zlevel: 1,
                        emphasis: { scale: true, scaleSize: 1.2 }
                    }
                ]
            };

            chartInstance.current.setOption(option);

            const handleResize = () => chartInstance.current?.resize();
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                chartInstance.current?.dispose();
            };
        }
    }, [mapType]);

    // 状态指示器组件
    const StatusIndicator = ({ status }: { status: StatusType }) => {
        const colors = {
            UP: 'bg-green-500',
            DEGRADED: 'bg-yellow-500',
            DOWN: 'bg-red-500',
            LOADING: 'bg-gray-300',
            BUILDING: 'bg-blue-500'
        };

        const labels = {
            UP: '在线',
            DEGRADED: '性能下降',
            DOWN: '离线',
            LOADING: '加载中',
            BUILDING: '建设中'
        };

        return (
            <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${colors[status]} ${status !== 'BUILDING' ? 'animate-pulse' : ''}`}></div>
                <span className="text-sm">{labels[status]}</span>
            </div>
        );
    };

    // 根据 mapType 切换表头文字和数据
    const leftColTitle = mapType === 'china' ? '省份' : '大洲';
    const rightColTitle = mapType === 'china' ? '城市' : '国家';
    const dataSource = mapType === 'china' ? chinaCenters : globalCenters;

    return (
        <section className="py-16 px-4 bg-gradient-to-b from-white to-indigo-50/20 text-center">
            <div className="mb-8">
                <h2 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-700 to-indigo-500 inline-block mb-2">
                    {mapType === 'world' ? '全球' : '中国'}数据中心分布
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                    高性能、低延迟的全球数据中心网络，为您的业务提供稳定可靠的服务支持
                </p>
            </div>

            <div className="flex justify-center mb-6">
                <div className="inline-flex rounded-md shadow-md" role="group">
                    <button
                        type="button"
                        onClick={() => setMapType('china')}
                        className={`px-5 py-2.5 text-sm font-medium rounded-l-lg transition-all duration-200 ${mapType === 'china'
                            ? 'bg-indigo-600 text-white shadow-inner'
                            : 'bg-white text-indigo-700 hover:bg-indigo-50 border border-indigo-200'}`}
                    >
                        中国分布
                    </button>
                    <button
                        type="button"
                        onClick={() => setMapType('world')}
                        className={`px-5 py-2.5 text-sm font-medium rounded-r-lg transition-all duration-200 ${mapType === 'world'
                            ? 'bg-indigo-600 text-white shadow-inner'
                            : 'bg-white text-indigo-700 hover:bg-indigo-50 border border-indigo-200'}`}
                    >
                        全球分布
                    </button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto bg-gradient-to-b from-white to-indigo-50/30 border border-indigo-100 shadow-lg rounded-3xl px-8 py-6">
                <div className="flex flex-col md:flex-row gap-8">
                    {/* 表格 */}
                    <div className="md:w-[38%] lg:w-[28%]">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-full">
                            <h3 className="text-xl font-semibold text-indigo-900 mb-4 flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm2 1h12v7H4V6zm0 9v-1h12v1H4z" clipRule="evenodd" />
                                </svg>
                                {mapType === 'world' ? '全球' : '中国'}数据中心详情
                            </h3>

                            <div className="bg-white rounded-2xl shadow border border-gray-200 overflow-hidden">
                                <div className="max-h-200 overflow-y-auto">
                                    <table className="min-w-full text-left">
                                        <thead className="sticky top-0 bg-indigo-50 text-indigo-700 text-xs uppercase">
                                            <tr>
                                                <th className="px-4 py-3 font-semibold">{leftColTitle}</th>
                                                <th className="px-4 py-3 font-semibold text-center">{rightColTitle}</th>
                                                <th className="px-4 py-3 font-semibold text-center">状态</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dataSource.map((center, index) => (
                                                <tr
                                                    key={index}
                                                    className="border-t last:border-b border-gray-100 hover:bg-indigo-50 transition-colors"
                                                >
                                                    <td className="px-4 py-3 text-sm text-gray-800">{center.region}</td>
                                                    <td className="px-4 py-3 text-sm text-indigo-600 text-center flex items-center justify-center gap-1">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-4 w-4 text-indigo-500 shrink-0"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M10 2a6 6 0 00-6 6c0 4 6 10 6 10s6-6 6-10a6 6 0 00-6-6zm0 8a2 2 0 110-4 2 2 0 010 4z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                        <span>{center.name}</span>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <StatusIndicator status={statusMap[center.tag] || 'LOADING'} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 地图 */}
                    <div className="md:w-[62%] lg:w-[72%]">
                        <div className="bg-white rounded-xl shadow-sm border border-indigo-100 p-4 h-full">
                            <div
                                ref={chartRef}
                                className="w-full h-[600px] rounded-lg overflow-hidden"
                            ></div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WorldMap;